import { useEffect, useCallback, useState } from "react";
import {
  Page,
  Card,
  Text,
  BlockStack,
  Badge,
  InlineStack,
  Button,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import { useParams, Link } from "react-router-dom";
import useIsPhone from "features/files/use-is-phone";
import ProjectDetails from "components/project/project-details/project-details";
import StageJourneyBar from "components/project/deal-progress-bar/stage-journey-bar";
import "./style.scss";
import { useSelector, useDispatch } from "react-redux";
import { fetchProjectDetails, setDestroy } from "./project-container.slice";
import {
  fetchProjectBoardWithCurrentFilters,
  markCardSeen,
} from "containers/project-board/project-board-container.slice";
import { updateProject } from "services/project.service";
import {
  celebrateWonDeal,
  formatWonToast,
} from "features/project-board/project-motion";
import { toast } from "sonner";
import ProjectDetailMainColumn, {
  DETAIL_TABS,
} from "components/project/project-detail-main-column/project-detail-main-column";
import ProjectOutcomeButtons from "components/project/project-outcome-buttons/project-outcome-buttons";
import ProjectDetailSkeleton from "./project-detail-skeleton";

const DETAIL_TAB_IDS = DETAIL_TABS.map((t) => t.id);

function ProjectContainer() {
  const { t } = useTranslation();
  const { companyId, projectBoardId, projectId } = useParams();
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("activity");
  const [stageMoving, setStageMoving] = useState(false);
  const [outcomeUpdating, setOutcomeUpdating] = useState(false);
  // On a phone the summary starts folded so the tabs are not pushed far down the screen.
  const isPhone = useIsPhone();
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);

  const { projectDetails, isError } = useSelector(
    (state) => state.projectReducer
  );
  const columnOrder = useSelector(
    (state) => state.projectBoardReducer.columnOrder || []
  );
  const columnsById = useSelector(
    (state) => state.projectBoardReducer.columnsById || {}
  );
  const boardName = useSelector(
    (state) => state.projectBoardReducer.boardName || ""
  );

  const loadProjectData = useCallback(() => {
    dispatch(fetchProjectDetails({ companyId, projectId }));
  }, [dispatch, companyId, projectId]);

  useEffect(() => {
    loadProjectData();
    return () => {
      dispatch(setDestroy(true));
    };
  }, [loadProjectData, dispatch]);

  // Need board columns for the stage journey (direct deep-link to detail).
  useEffect(() => {
    if (companyId && projectBoardId && columnOrder.length === 0) {
      dispatch(fetchProjectBoardWithCurrentFilters(companyId, projectBoardId));
    }
  }, [dispatch, companyId, projectBoardId, columnOrder.length]);

  useEffect(() => {
    if (companyId && projectId) {
      dispatch(markCardSeen(companyId, projectId));
    }
  }, [dispatch, companyId, projectId]);

  const handleOnFileChange = () => {};

  const updateHandler = async (val, type) => {
    const formData = {
      id: projectId,
      projectBoardColumn:
        type === "project_column" ? val : projectDetails.projectBoardColumn.id,
      name: projectDetails.name,
      description: projectDetails.description,
      user: type === "user" ? val : projectDetails.user?.id,
      customer: projectDetails.customer?.id,
      order: projectDetails.order,
      products: projectDetails.products,
      isImportant: projectDetails.isImportant,
      isResolved: projectDetails.isResolved,
      dueDate: projectDetails.dueDate,
      outcome: type === "outcome" ? val : projectDetails.outcome || null,
      valueEur:
        projectDetails.valueEur === undefined ? null : projectDetails.valueEur,
      contactName: projectDetails.contactName || null,
      contactEmail: projectDetails.contactEmail || null,
      contactPhone: projectDetails.contactPhone || null,
      contactLinkedin: projectDetails.contactLinkedin || null,
      contactWhatsapp: projectDetails.contactWhatsapp || null,
      contactViber: projectDetails.contactViber || null,
    };
    try {
      const prevOutcome = projectDetails.outcome || null;
      await updateProject(formData, companyId);
      if (type === "outcome" && val === "WON" && prevOutcome !== "WON") {
        await celebrateWonDeal();
        toast.success(formatWonToast(projectDetails.valueEur));
      }
      dispatch(fetchProjectBoardWithCurrentFilters(companyId, projectBoardId));
      if (projectId) dispatch(fetchProjectDetails({ companyId, projectId }));
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  const handleStageSelect = async (columnId) => {
    if (
      stageMoving ||
      !columnId ||
      columnId === projectDetails?.projectBoardColumn?.id
    ) {
      return;
    }
    setStageMoving(true);
    try {
      await updateHandler(columnId, "project_column");
    } catch (e) {
      toast.error(
        t("project_stage_move_failed", {
          defaultValue: "Could not move to that stage.",
        })
      );
    } finally {
      setStageMoving(false);
    }
  };

  const handleOutcomeSet = async (outcome) => {
    if (outcomeUpdating) return;
    setOutcomeUpdating(true);
    try {
      await updateHandler(outcome, "outcome");
    } catch (e) {
      toast.error(
        t("project_outcome_update_failed", {
          defaultValue: "Could not update outcome.",
        })
      );
    } finally {
      setOutcomeUpdating(false);
    }
  };

  const hasCurrentProjectDetails =
    projectDetails && `${projectDetails.id}` === `${projectId}`;

  if (!hasCurrentProjectDetails) {
    if (isError) {
      return (
        <Page
          backAction={{
            content: "Project-Board",
            url: `/app/${companyId}/project-boards/${projectBoardId}`,
          }}
          title={t("project", { defaultValue: "Project" })}
        >
          <Card roundedAbove="sm">
            <BlockStack gap="300" inlineAlign="center">
              <Text as="p" tone="subdued">
                {t("board_load_failed", {
                  defaultValue: "Couldn't load this project.",
                })}
              </Text>
              <Button onClick={loadProjectData}>
                {t("retry", { defaultValue: "Retry" })}
              </Button>
            </BlockStack>
          </Card>
        </Page>
      );
    }
    return <ProjectDetailSkeleton />;
  }

  const projectTitle =
    projectDetails.customer?.name || projectDetails.customer?.externalCode
      ? `${projectDetails.customer?.name || ""} ${
          projectDetails.customer?.externalCode
            ? `[${projectDetails.customer.externalCode}]`
            : ""
        }`
      : "";

  const currentColumnId = projectDetails.projectBoardColumn?.id || "";
  const currentStageName =
    columnsById[currentColumnId]?.name ||
    projectDetails.projectBoardColumn?.name ||
    "";

  return (
    <div className="project-detail-page">
      <div className="project-detail-chrome">
        <div className="flex items-center gap-3 border-b border-[var(--p-color-border)] bg-[var(--p-color-bg-surface)] px-5 py-3 max-md:flex-wrap max-md:px-4">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 left-full hidden w-16 border-b border-[var(--p-color-border)] bg-[var(--p-color-bg-surface)] lg:block"
          />
          <Link
            to={`/app/${companyId}/project-boards/${projectBoardId}`}
            className="shrink-0 rounded px-2 py-1 text-sm font-medium text-[var(--p-color-text-secondary)] hover:bg-[var(--p-color-bg-surface-secondary)] hover:text-[var(--p-color-text)]"
          >
            ← {t("project-boards", { defaultValue: "Boards" })}
          </Link>
          <h1 className="min-w-0 flex-1 truncate text-base font-semibold text-[var(--p-color-text)] max-md:basis-full max-md:whitespace-normal max-md:break-words">
            {projectTitle || projectDetails.name}
          </h1>
          <div className="flex shrink-0 items-center gap-3 max-md:w-full max-md:flex-wrap">
            <InlineStack gap="200">
              {projectDetails.valueEur !== null &&
                projectDetails.valueEur !== undefined && (
                  <Badge tone="info">
                    {new Intl.NumberFormat("lt-LT", {
                      style: "currency",
                      currency: "EUR",
                      maximumFractionDigits: 2,
                    }).format(Number(projectDetails.valueEur))}
                  </Badge>
                )}
              {projectDetails.outcome === "DELETED" && (
                <Badge>
                  {t("project_outcome_deleted", { defaultValue: "Deleted" })}
                </Badge>
              )}
              {projectDetails.isImportant && (
                <Badge tone="critical">{t("important")}</Badge>
              )}
            </InlineStack>
            <ProjectOutcomeButtons
              outcome={projectDetails.outcome}
              disabled={outcomeUpdating || stageMoving}
              onSetOutcome={handleOutcomeSet}
            />
          </div>
        </div>

        {currentColumnId && columnOrder.length > 0 && (
          <StageJourneyBar
            columnOrder={columnOrder}
            columnsById={columnsById}
            currentColumnId={currentColumnId}
            onSelectColumn={handleStageSelect}
            disabled={stageMoving}
          />
        )}

        {(boardName || currentStageName) && (
          <div className="flex items-center gap-1 border-b border-[var(--p-color-border)] bg-[var(--p-color-bg-surface)] px-5 py-2 text-xs text-[var(--p-color-text-secondary)]">
            {boardName && <span>{boardName}</span>}
            {boardName && currentStageName && <span>→</span>}
            {currentStageName && (
              <span className="font-medium text-[var(--p-color-text)]">
                {currentStageName}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="project-detail-body">
        <aside className="project-detail-summary">
          {isPhone && (
            <button
              type="button"
              aria-expanded={isSummaryOpen}
              onClick={() => setIsSummaryOpen((isOpen) => !isOpen)}
              className="mb-2 flex min-h-11 w-full items-center justify-between rounded-lg border border-[var(--p-color-border)] bg-[var(--p-color-bg-surface)] px-4 text-sm font-semibold text-[var(--p-color-text)]"
            >
              {t("project_summary", { defaultValue: "Summary" })}
              <ChevronDown
                size={16}
                className={isSummaryOpen ? "rotate-180" : undefined}
              />
            </button>
          )}
          {(!isPhone || isSummaryOpen) && (
            <ProjectDetails
              id={projectId}
              projectBoardId={projectBoardId}
              name={projectDetails.name}
              description={projectDetails.description}
              projectBoardColumnEntity={projectDetails.projectBoardColumn}
              userEntity={projectDetails.user}
              updateHandler={updateHandler}
              dueDate={projectDetails.dueDate}
              reporterEntity={projectDetails.reporter}
              createdAt={projectDetails.createdAt}
              valueEur={projectDetails.valueEur}
              contactName={projectDetails.contactName}
              contactEmail={projectDetails.contactEmail}
              contactPhone={projectDetails.contactPhone}
              contactLinkedin={projectDetails.contactLinkedin}
              contactWhatsapp={projectDetails.contactWhatsapp}
              contactViber={projectDetails.contactViber}
              hideColumnSelect
            />
          )}
        </aside>

        <div className="project-detail-scroll">
          <ProjectDetailMainColumn
            companyId={companyId}
            projectId={projectId}
            projectDetails={projectDetails}
            activeTab={DETAIL_TAB_IDS.includes(activeTab) ? activeTab : "activity"}
            onTabChange={setActiveTab}
            onFileChange={handleOnFileChange}
          />
        </div>
      </div>
    </div>
  );
}

export default ProjectContainer;
